import { useEffect, useRef, useState } from "react";
import { api } from "./lib/api";
import { currentSpeed, roadLimit, type SpeedRoad } from "./lib/speed";
import type { LocationFix } from "./lib/types";
import type { Settings } from "./lib/storage";

export function Speedometer({fix,settings,onSettings}:{fix:LocationFix|null;settings:Settings;onSettings:()=>void}) {
  const [roads,setRoads]=useState<SpeedRoad[]>([]),[now,setNow]=useState(Date.now());
  const latest=useRef(fix); latest.current=fix;
  const fetched=useRef({lat:999,lng:999,at:0});
  const audio=useRef<AudioContext|null>(null), warned=useRef(false), lastBeep=useRef(0);
  const isRoad = !["bicycle", "scooter", "bus", "walking"].includes(settings.travelMode);
  const enabled=settings.showSpeed || settings.speedWarning || settings.speedSound;
  useEffect(()=>{if(!enabled)return; const id=setInterval(()=>setNow(Date.now()),1000);return()=>clearInterval(id)},[enabled]);
  useEffect(()=>{
    if(!enabled || !isRoad){setRoads([]);return;}
    let alive=true,busy=false; const controller=new AbortController();
    async function load(){
      const p=latest.current, t=Date.now(); if(busy || !p || currentSpeed(p,t)===null || document.hidden)return;
      if(t-fetched.current.at<30000 || (t-fetched.current.at<240000 && Math.hypot(p.lat-fetched.current.lat,(p.lng-fetched.current.lng)*Math.cos(p.lat*Math.PI/180))*111320<500))return;
      fetched.current={...p,at:t}; busy=true;
      try {const result=await api.roadSpeeds(p,controller.signal);if(alive)setRoads(result.roads);}
      catch{if(alive)setRoads([]);}finally{busy=false;}
    }
    void load(); const id=setInterval(()=>void load(),5000);
    return()=>{alive=false;controller.abort();clearInterval(id);setRoads([])};
  },[enabled,isRoad]);
  useEffect(()=>{
    if(!settings.speedSound)return;
    const unlock=()=>{try{audio.current??=new AudioContext();void audio.current.resume().catch(()=>{});}catch{}};
    document.addEventListener('pointerdown',unlock);
    return()=>document.removeEventListener('pointerdown',unlock);
  },[settings.speedSound]);
  useEffect(()=>()=>{void audio.current?.close()},[]);
  const speed=currentSpeed(fix,now), limit=isRoad && now-fetched.current.at<300000?roadLimit(fix,roads,now):null;
  const over=speed!==null && limit!==null && speed>limit.kph+settings.speedTolerance;
  useEffect(()=>{
    if(!over){if(speed===null || !limit || speed<=limit.kph+settings.speedTolerance-2)warned.current=false;return;}
    if(warned.current)return; warned.current=true;
    if(!settings.speedSound || document.hidden || Date.now()-lastBeep.current<30000 || audio.current?.state!=='running')return;
    lastBeep.current=Date.now(); const ctx=audio.current, tone=ctx.createOscillator(),gain=ctx.createGain();
    tone.frequency.value=740;gain.gain.setValueAtTime(0,ctx.currentTime);gain.gain.linearRampToValueAtTime(.12,ctx.currentTime+.02);gain.gain.exponentialRampToValueAtTime(.001,ctx.currentTime+.22);
    tone.connect(gain);gain.connect(ctx.destination);tone.start();tone.stop(ctx.currentTime+.24);tone.onended=()=>{tone.disconnect();gain.disconnect()};
  },[over,speed,limit?.kph,settings.speedSound,settings.speedTolerance]);
  if(!settings.showSpeed && !(over && settings.speedWarning))return null;
  return <button className={'speedometer '+(over && settings.speedWarning?'speed-over':'')} aria-label="מהירות נוכחית והגדרות התראה" onClick={onSettings}>
    {settings.showSpeed && <><span className="speed-reading"><strong>{speed===null?'—':Math.round(speed)}</strong><small>קמ״ש · GPS</small></span>{isRoad && <span className="speed-limit" title={limit?.name || 'אין נתון מהירות לכביש'}>{limit?Math.round(limit.kph):'—'}</span>}</>}
    {over && settings.speedWarning && <span role="status">מעל המהירות המוגדרת</span>}
  </button>;
}
